import { FC, memo, ReactChild } from 'react';
import loadable from '@loadable/component';

import * as Meta from '@Queries/Meta';
import usePromoSettings from '@Promo/usePromoSettings';
import { CountryCode } from '@Types/Base';
import { PromoCardData } from '@Promo/typings';

interface LoaderProps {
  country: CountryCode;
}

interface Module {
  default: { recommendations: PromoCardData[]; promotions: PromoCardData[] };
}

export interface PromoCardsProps {
  section: 'recommendations' | 'promotions';
  render: ({ cards }: { cards: PromoCardData[] }) => ReactChild;
  onLoad?: (cards: PromoCardData[]) => void;
}

const Loader = loadable.lib<LoaderProps, FC<LoaderProps>>(
  (props) => import(`@Promo/promo-cards/${props.country}.ts`),
);

const PromoCards: FC<PromoCardsProps> = (props) => {
  const { render, onLoad, section } = props;
  const { now } = usePromoSettings();
  const meta = Meta.useMeta();

  return (
    <Loader country={meta.data.country}>
      {(module: Module) => {
        let cards = [];
        if (module.default) {
          cards =
            section === 'promotions'
              ? module.default.promotions || []
              : module.default.recommendations || [];
        }

        const filteredCards = cards.filter((card) => {
          // По статусу
          if (card.disabled) return false;

          // По дате начала, окончания
          if (card.startDate && now < card.startDate) return false;
          if (card.endDate && now > card.endDate) return false;

          // Фильтруем по регионам
          if (card.regionIds && !card.regionIds.includes(meta.data.region.id)) return false;

          // Исключаем регионы
          if (card.excludeRegionIds && card.excludeRegionIds.includes(meta.data.region.id)) {
            return false;
          }

          return true;
        });

        if (onLoad) onLoad(filteredCards);

        return render({ cards: filteredCards });
      }}
    </Loader>
  );
};

export default memo(PromoCards);
